import { Alert, Stack } from "@mui/material";
import { FormikErrors } from "formik";
import { useEffect, useRef, useState } from "react";

import AppButton from "@/common/components/Button/AppButton";
import {
  GenericDamageViewerMode,
  VehicleVisualModelGenericDamageEditorRefHandle,
  VisualModelEditorOfGenericDamage,
} from "@/common/components/Entity/VehicleVisualModel/Viewer/v2/GenericDamage/VisualModelEditorOfGenericDamage";
import { EntityHelper } from "@/common/helpers/entity";
import { DamageCostEvaluationFormPermissions } from "@/common/helpers/entity/damageCostEvaluation";
import { TypeHelper } from "@/common/helpers/type";
import { Mapper } from "@/common/mapping/mapper";
import { VehicleDamageDataInputDtoLocal } from "@/common/mapping/mappers/vehicleDamage";
import { GenericDamage } from "@/common/ts/visualModel";

import { VehicleDamageDataViewModal } from "../../VehicleDamage/View/VehicleDamageDataViewModal";

/** Entity specific type. */
export interface GenericDamageOfVehicleDamageDataInputDto extends GenericDamage<VehicleDamageDataInputDtoLocal> {
  data: VehicleDamageDataInputDtoLocal;
}

function mapToGenericDamage(
  source: VehicleDamageDataInputDtoLocal,
  dest?: Partial<GenericDamageOfVehicleDamageDataInputDto>,
): GenericDamageOfVehicleDamageDataInputDto {
  return Mapper.visualModel.mapVehicleDamageDataInputDtoToGenericDamage(
    source,
    dest,
  ) as GenericDamageOfVehicleDamageDataInputDto;
}

function mapToGenericDamageOnInit(
  source: VehicleDamageDataInputDtoLocal,
): GenericDamageOfVehicleDamageDataInputDto {
  return mapToGenericDamage({
    ...source,
    idBeforeUpdateLocal: null, // ensure no stale data
  });
}

export interface DamageCostEvaluationDamageEditorProps {
  mode: GenericDamageViewerMode;
  formPerms: DamageCostEvaluationFormPermissions | null;
  vehicleId: string;
  initialDamagesData: VehicleDamageDataInputDtoLocal[];
  /** Enables live state sync and display of damages. Only for view mode. */
  damagesData?: VehicleDamageDataInputDtoLocal[];
  /** On init opens update modal for the damage data. */
  initialDamageDataToUpdate?: Nil<VehicleDamageDataInputDtoLocal>;
  errors: Nil<Array<FormikErrors<VehicleDamageDataInputDtoLocal> | undefined>>;
  onCancel: () => void;
  onSave: (newValues: VehicleDamageDataInputDtoLocal[]) => void;
}

/** Workflow (no live state sync): initial damages -> local state -> local changes -> save -> new damages. */
export function DamageCostEvaluationDamageEditor({
  mode,
  formPerms,
  vehicleId,
  initialDamagesData,
  damagesData,
  initialDamageDataToUpdate,
  errors,
  onCancel,
  onSave,
}: DamageCostEvaluationDamageEditorProps) {
  const isViewMode = mode === GenericDamageViewerMode.View;
  const isEditMode = mode === GenericDamageViewerMode.Edit;

  const damageEditorRef =
    useRef<
      VehicleVisualModelGenericDamageEditorRefHandle<GenericDamageOfVehicleDamageDataInputDto>
    >(null);

  const [genericDamages, setGenericDamages] = useState(() =>
    initialDamagesData.map((x) => mapToGenericDamageOnInit(x)),
  );
  const [vehicleDamageDataToView, setVehicleDamageDataToView] =
    useState<Nil<VehicleDamageDataInputDtoLocal>>(null);

  useEffect(() => {
    if (isViewMode && damagesData) {
      setGenericDamages(damagesData?.map((x) => mapToGenericDamageOnInit(x)) || []);
    }
  }, [isViewMode, damagesData]);

  // on init open update modal if requested
  const isHandledInitialDamageDataToUpdateRef = useRef(false);
  useEffect(() => {
    if (
      !isHandledInitialDamageDataToUpdateRef.current &&
      isEditMode &&
      damageEditorRef.current &&
      initialDamageDataToUpdate
    ) {
      const genericDamage = mapToGenericDamageOnInit(initialDamageDataToUpdate);
      damageEditorRef.current?.update(genericDamage);
      isHandledInitialDamageDataToUpdateRef.current = true;
    }
  }, [isEditMode, initialDamageDataToUpdate, damageEditorRef.current]);

  return (
    <Stack gap={1}>
      {isEditMode && (
        <Alert severity='info'>
          Editing here creates a local copy of a damage and marks you as inspector.
        </Alert>
      )}

      {/* Damage editor */}
      <VisualModelEditorOfGenericDamage
        ref={damageEditorRef}
        mode={mode}
        vehicleId={vehicleId}
        visualModelId={undefined}
        visualModel={undefined}
        isAutoPickVisualModel
        damages={genericDamages || []}
        errors={errors?.map((x) =>
          x
            ? {
                ...x,
                partTypeId: x?.partTypeId,
                damageTypeId: x?.damageTypeId,
                area: x?.area,
                projection: x?.projection,
                point: x?.point,
                notes: x?.notes,
                attachments: x?.attachments,
              }
            : undefined,
        )}
        isDisplayOld
        isDisplayList={isEditMode}
        isViewOldEnabled
        isViewEnabled={isViewMode}
        isCreateEnabled={isEditMode && formPerms?.canEdit && formPerms?.canAddDamages}
        isUpdateEnabled={isEditMode && formPerms?.canEdit && formPerms?.canEditDamages}
        isDeleteEnabled={isEditMode && formPerms?.canEdit && formPerms?.canRemoveDamages}
        isMoveEnabled={isEditMode && formPerms?.canEdit && formPerms?.canEditDamages}
        isSplitEnabled={isEditMode && formPerms?.canEdit && formPerms?.canEditDamages}
        isMergeEnabled={isEditMode && formPerms?.canEdit && formPerms?.canEditDamages}
        oldDamagesDisplay={{
          isStateTogglesEnabled: true,
        }}
        onView={(damage) => {
          setVehicleDamageDataToView(damage.data);
        }}
        // whenever damage data is created/updated locally it is switched to inline mode
        onCreated={(newValue) => {
          if (!isEditMode) {
            return;
          }
          let newDamageData =
            Mapper.visualModel.mapGenericDamageToVehicleDamageDataInputDto(newValue);
          newDamageData = EntityHelper.switchEntityDataToInlineMode(newDamageData);
          const newGenericDamage: GenericDamageOfVehicleDamageDataInputDto = mapToGenericDamage(
            newDamageData,
            newValue,
          );
          const newGenericDamages = [...genericDamages, newGenericDamage];
          setGenericDamages(newGenericDamages);

          console.log("DamageCostEvaluationDamageEditor.onCreated", {
            newValue,
            newDamageData0:
              Mapper.visualModel.mapGenericDamageToVehicleDamageDataInputDto(newValue),
            newDamageData,
            newGenericDamage,
          });
        }}
        onUpdated={(newValue) => {
          if (!isEditMode) {
            return;
          }
          const oldId = newValue.id;
          let newDamageData =
            Mapper.visualModel.mapGenericDamageToVehicleDamageDataInputDto(newValue);
          newDamageData = EntityHelper.switchEntityDataToInlineMode(newDamageData);
          newDamageData.idBeforeUpdateLocal = oldId; // temporarily preserve old id
          const newGenericDamage: GenericDamageOfVehicleDamageDataInputDto =
            mapToGenericDamage(newDamageData);
          const newGenericDamages = genericDamages.map((x) =>
            x.id === oldId ? newGenericDamage : x,
          );
          setGenericDamages(newGenericDamages);

          console.log("DamageCostEvaluationDamageEditor.onUpdated", {
            oldId,
            newValue,
            newDamageData0:
              Mapper.visualModel.mapGenericDamageToVehicleDamageDataInputDto(newValue),
            newDamageData,
            newGenericDamage,
            newGenericDamages,
            genericDamages,
          });

          return { newValue: newGenericDamage };
        }}
        onDeleted={(newValue) => {
          if (!isEditMode) {
            return;
          }
          const newGenericDamages = genericDamages.filter((x) => x.id !== newValue.id);
          setGenericDamages(newGenericDamages);

          console.log("DamageCostEvaluationDamageEditor.onDeleted", {
            newValue,
            newGenericDamages,
          });
        }}
        onManyChanged={(params) => {
          if (!isEditMode) {
            return;
          }

          let newGenericDamages = [...genericDamages];

          // added
          if (TypeHelper.isNotEmptyArray(params.added)) {
            const newGenericDamagesAdded =
              params.added.map((genericDamage) => {
                let newDamageData =
                  Mapper.visualModel.mapGenericDamageToVehicleDamageDataInputDto(genericDamage);
                newDamageData = EntityHelper.switchEntityDataToInlineMode(newDamageData);
                const newGenericDamage: GenericDamageOfVehicleDamageDataInputDto =
                  mapToGenericDamage(newDamageData, genericDamage);
                return newGenericDamage;
              }) || [];
            newGenericDamages = genericDamages.concat(newGenericDamagesAdded);
          }

          // updated
          if (TypeHelper.isNotEmptyArray(params.updated)) {
            const newGenericDamagesUpdatedPair = params.updated.map((genericDamage) => {
              const oldGenericDamage = genericDamage;
              let newDamageData =
                Mapper.visualModel.mapGenericDamageToVehicleDamageDataInputDto(oldGenericDamage);
              newDamageData = EntityHelper.switchEntityDataToInlineMode(newDamageData);
              newDamageData.idBeforeUpdateLocal = oldGenericDamage.id; // temporarily preserve old id
              const newGenericDamage: GenericDamageOfVehicleDamageDataInputDto = mapToGenericDamage(
                newDamageData,
                oldGenericDamage,
              );
              return { oldGenericDamage, newGenericDamage };
            });
            newGenericDamages = newGenericDamages.map((genericDamage) => {
              const newPair = newGenericDamagesUpdatedPair.find(
                (x) => x.oldGenericDamage.id === genericDamage.id,
              );
              return newPair ? newPair.newGenericDamage : genericDamage;
            });
          }

          // removed
          if (TypeHelper.isNotEmptyArray(params.removed)) {
            const removedIds = params.removed.map((x) => x.id);
            newGenericDamages = newGenericDamages.filter(
              (genericDamage) => !removedIds.includes(genericDamage.id),
            );
          }

          console.log("DamageCostEvaluationDamageEditor.onManyChanged", {
            params,
            newGenericDamages,
          });

          setGenericDamages(newGenericDamages);
        }}
      />

      {/* Primary actions */}
      {isEditMode && (
        <Stack
          direction={{ xxs: "column", md: "row" }}
          gap={1}
          sx={{
            justifyContent: { xxs: "flex-start", md: "flex-end" },
            alignItems: { xxs: "flex-start", md: "flex-start" },
          }}
        >
          <AppButton
            variant='outlined'
            size='medium'
            color='text'
            onClick={() => {
              onCancel();
            }}
          >
            Cancel
          </AppButton>

          <AppButton
            variant='contained'
            size='medium'
            color='primary'
            onClick={() => {
              const newDamagesData = genericDamages.map((x) =>
                Mapper.visualModel.mapGenericDamageToVehicleDamageDataInputDto(x),
              );

              console.log("DamageCostEvaluationDamageEditor.onSave", {
                newDamagesData,
              });

              onSave(newDamagesData);
            }}
          >
            Save
          </AppButton>
        </Stack>
      )}

      {/* Vehicle damage data view modal */}
      {vehicleDamageDataToView && (
        <VehicleDamageDataViewModal
          open={!!vehicleDamageDataToView}
          onClose={() => {
            setVehicleDamageDataToView(null);
          }}
          viewProps={{
            entityData: vehicleDamageDataToView,
          }}
        />
      )}
    </Stack>
  );
}
